import { Button, Card, Group, Stack, Table, Text } from '@mantine/core'
import { IconRefresh, IconWifi } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { useCameraPing } from '@/components/cameras/CameraDetails/useCameraPing'
import { useCameraRefresh } from '@/components/cameras/CameraDetails/useCameraRefresh'
import { OnlineStatus } from '@/components/devices/OnlineStatus/OnlineStatus'
import { Camera } from '@/types/camera'
import { Device } from '@/types/device'
import { getRelativeTimeFromNow } from '@/utils/date'

const formatResolution = (resolution: Camera['resolution']): string => {
  if (typeof resolution === 'object' && resolution !== null) {
    return `${resolution.width}x${resolution.height}`
  }
  return resolution
}

type DeploymentCameraInfoProps = {
  camera: Camera
  device: Device
  onRefresh: () => void
}

export const DeploymentCameraInfo = ({
  camera,
  device,
  onRefresh
}: DeploymentCameraInfoProps) => {
  const { isRefreshing, refreshCameraImage } = useCameraRefresh({
    deviceId: device.id,
    cameraId: camera.camera_id,
    onRefresh
  })

  const { isPinging, pingCamera } = useCameraPing({
    deviceId: device.id,
    cameraId: camera.camera_id,
    onSuccess: onRefresh
  })

  return (
    <Card padding="md" withBorder>
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <Text fw={600} size="sm">
              {camera.camera_name}
            </Text>
            <OnlineStatus
              status={device.active && camera.connected ? 'online' : 'offline'}
              withText={false}
            />
          </Group>

          <Group gap="xs">
            <Button
              size="xs"
              leftSection={<IconWifi size={14} />}
              loading={isPinging}
              onClick={() => void pingCamera()}
            >
              <FormattedMessage id="deployments.details.camera.ping" />
            </Button>

            <Button
              size="xs"
              variant="outline"
              leftSection={<IconRefresh size={14} />}
              loading={isRefreshing}
              onClick={() => void refreshCameraImage()}
            >
              <FormattedMessage id="deployments.details.camera.refreshImage" />
            </Button>
          </Group>
        </Group>

        <Table variant="vertical" layout="fixed">
          <Table.Tbody>
            <Table.Tr>
              <Table.Th>
                <FormattedMessage id="cameras.fields.fps" />
              </Table.Th>
              <Table.Td>{camera.fps}</Table.Td>
            </Table.Tr>

            <Table.Tr>
              <Table.Th>
                <FormattedMessage id="cameras.fields.resolution" />
              </Table.Th>
              <Table.Td>{formatResolution(camera.resolution)}</Table.Td>
            </Table.Tr>

            <Table.Tr>
              <Table.Th>
                <FormattedMessage id="deployments.details.camera.lastScreenshotTime" />
              </Table.Th>
              <Table.Td>
                {camera.last_screenshot_time
                  ? getRelativeTimeFromNow(camera.last_screenshot_time)
                  : '-'}
              </Table.Td>
            </Table.Tr>

            <Table.Tr>
              <Table.Th>
                <FormattedMessage id="deployments.details.camera.lastPingTime" />
              </Table.Th>
              <Table.Td>
                {camera.last_ping_time
                  ? getRelativeTimeFromNow(camera.last_ping_time)
                  : '-'}
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  )
}
